import { useState, useCallback, useEffect, useRef } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import Request from '../services/fetchDataService';
import { mutate } from 'swr';

export function useSecureRequest() {
  const { config } = useConfig();
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(config.token);

  // Keep ref updated to detect token changes
  useEffect(() => {
    tokenRef.current = config.token;
  }, [config.token]);

  const securePost = useCallback(async (url: string, data: any) => {
    setLoading(true);
    
    // Helper to wait for token refresh
    const waitForTokenRefresh = async () => {
      const startToken = tokenRef.current;
      let retries = 0;
      // Poll every 100ms for up to 3 seconds
      while (tokenRef.current === startToken && retries < 30) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      return tokenRef.current;
    };

    try {
      return await Request.post(url, config.key, data);
    } catch (error: any) {
      if (error?.status === 240) {
        // Token expired. Trigger global refresh via SWR key mutation.
        // We look for keys starting with /security/auth/
        await mutate(
            (k: any) => Array.isArray(k) && typeof k[0] === 'string' && k[0].includes('/security/auth/'),
            undefined,
            { revalidate: true }
        );

        // Wait for the new token to propagate to ConfigContext
        const newToken = await waitForTokenRefresh();

        // Retry the request
        // If the original data contained a csrfToken, update it with the new one
        const retryData = { ...data };
        if (retryData.csrfToken) {
          retryData.csrfToken = newToken;
        }

        // Retry with the (possibly same) key and updated data
        return await Request.post(url, config.key, retryData);
      }
      // If not 240, or retry failed, rethrow
      throw error;
    } finally {
      setLoading(false);
    }
  }, [config.key]);

  return { securePost, loading };
}
